import { useGetAdditionalEmailFromMyExamCard } from '@exam-room/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { Card, InfoRoundedIcon, LoadingSpinner } from '../../../components';
import { useGetExamEventSettings } from '../../../hooks';
import { useAuth, useConfig, useExamEvent } from '../../../providers';
import { PATHS } from '../../../routes';
import { ExamineeCertificateInfoCard, SubmittedIcon } from '../completed/components';
import LogoutButtonWithCountdown from '../containers/logout-button-with-countdown';

import { SubmittedContinueButton } from './components';

function Submitted() {
  const { examCardId } = useParams();
  const config = useConfig();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const {
    loading: isGetAdditionalEmailFromMyExamCardLoading,
    data: getAdditionalEmailFromMyExamCardData,
  } = useGetAdditionalEmailFromMyExamCard();

  const { isLoading: isGetEventSettingsLoading, settings } = useGetExamEventSettings({
    cardId: examCardId as string,
  });

  const {
    examEvent: { metadata: examEventMetadata },
    closestExam,
  } = useExamEvent();

  const isFinished = useMemo(() => {
    const examEvents = [...examEventMetadata.events].sort((a, b) => a.order - b.order);
    const lastExamEvent = examEvents[examEvents.length - 1];

    return closestExam === null || closestExam.examId === lastExamEvent.examId;
  }, [examEventMetadata]);

  const email = user?.email;
  const additionalEmail = getAdditionalEmailFromMyExamCardData?.erGetMyExamCard.additionalEmail;

  const examResultSettings = settings.result;
  const shouldDisplayCertificateInfo = examResultSettings.hasCertificate;
  const isQuestionnaireEnabled = !!settings.questionnaire;

  const isLoading = isGetAdditionalEmailFromMyExamCardLoading || isGetEventSettingsLoading;

  if (isLoading) {
    return (
      <div className='grid h-dvh w-screen place-items-center'>
        <LoadingSpinner />
      </div>
    );
  }

  const handleContinueClick = () => {
    if (isQuestionnaireEnabled) {
      navigate(generatePath(PATHS.Exam.Questionnaire, { examCardId }));
      return;
    }

    navigate(generatePath(PATHS.Exam.Processing, { examCardId }));
  };

  const handleLogout = async () => {
    await logout();
    navigate(PATHS.Home);
  };

  return (
    <div className='flex size-full flex-col overflow-auto'>
      <div className='flex-grow p-6 text-center'>
        <Card className='mx-auto flex size-full h-max max-w-[800px] flex-col items-center gap-6 p-10'>
          {isFinished ? (
            <>
              <SubmittedIcon />
              <div className='flex flex-col items-center gap-2'>
                <h3>{t('examEnding.submitAnswer.title')}</h3>
                <p className='text-text-secondary text-body1'>
                  {t(
                    isQuestionnaireEnabled
                      ? 'examEnding.submitAnswer.description.part1'
                      : 'examEnding.submitAnswer.description.part1NoQuestionnaire',
                  )}
                </p>
              </div>
              <div className='grid w-full max-w-[420px] gap-2 rounded-md border border-gray-300 p-4'>
                <h4>
                  {user?.firstname} {user?.lastname}
                </h4>
              </div>
              {shouldDisplayCertificateInfo ? (
                <div className='grid w-full max-w-[420px] gap-3'>
                  <ExamineeCertificateInfoCard email={email} additionalEmail={additionalEmail} />
                </div>
              ) : null}
              <div className='flex w-full flex-col items-center gap-3'>
                <SubmittedContinueButton
                  variant={isQuestionnaireEnabled ? 'questionnaire' : 'completed'}
                  onClick={handleContinueClick}
                  countdownTime={config.submitted?.continueButtonCountdownTimeSeconds}
                />
                <div className='flex items-center gap-2'>
                  <InfoRoundedIcon className='text-grey-500 text-xl' />
                  <p className='text-text-secondary text-sm'>
                    {t('examEnding.incorrectInformation')}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <SubmittedIcon />
              <div className='flex flex-col items-center gap-2'>
                <h3>{t('examEnding.submitAnswer.title')}</h3>
                <p className='text-text-secondary text-body1'>
                  {t('examEnding.submitAnswer.description.part2')}
                </p>
              </div>
              <LogoutButtonWithCountdown
                className='w-full max-w-[420px]'
                onLogout={handleLogout}
                countdownTime={120}
              />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

const SubmittedRoute = {
  path: PATHS.Exam.Submitted,
  element: <Submitted />,
};

export default SubmittedRoute;
